// Config and utils for filters that can be applied to an item type
import isEqual from 'lodash/isEqual';
import {DateTime} from 'luxon';

type StringMap = {
  [key: string]: string;
};

type BooleanMap = {
  [key: string]: boolean;
};

type DateRange = [string, string];

export type FilterValueType = string[] | number[] | undefined;

type Filter = {
  values?: FilterValueType;
  defaults?: FilterValueType;
  field_name: string;
  type: string;
};

type RangeFilterConfig = {gte: string | number; lte: string | number};

export type StringInFilterConfig = string[];

export type NumberInFilterConfig = number[];

type APIFilterConfig =
  | RangeFilterConfig
  | StringInFilterConfig
  | NumberInFilterConfig;

type APIFilter = {
  type: string;
  config: APIFilterConfig;
  field_name?: string;
};

// Utility functions for scaling and formatting
const toPercent = (x: number) => Math.round(x * 100);
const fromPercent = (x: number) => x / 100;
const formatPercentRange = (values: number[]) => values.map(toPercent);

// to not lose decimal precision but still round where possible, multiply by 100 before rounding and then turn back into a decimal
const formatMeterValue = (x: number) => Math.round(x * 100) / 100;
const formatMetersRange = (values: number[]) =>
  values.map(value => formatMeterValue(value));

const formatDegrees = (x: number) => Math.round(x);
const roundValues = (values: number[]) => values.map(Math.round);

export const KEYS = {
  CLOUD_COVER: 'cloud_cover',
  GSD: 'gsd',
  OFF_NADIR_ANGLE: 'view_angle',
  SUN_AZIMUTH: 'sun_azimuth',
  SUN_ELEVATION: 'sun_elevation',
  GROUND_CONTROL: 'ground_control',
  QUALITY_CATEGORY: 'quality_category',
};

export const PS_INSTRUMENTS = {PS2: false, 'PS2.SD': false, 'PSB.SD': false};

export const INSTRUMENT_LABELS: StringMap = {
  PS2: 'Dove Classic',
  'PS2.SD': 'Dove R',
  'PSB.SD': 'Super Dove',
};

export const formatInstrumentLabel = (instrument: string) =>
  INSTRUMENT_LABELS[instrument]
    ? `${INSTRUMENT_LABELS[instrument]} (${instrument})`
    : instrument;

export const PUBLISHING_STAGES_DEFAULT: BooleanMap = {
  preview: false,
  standard: true,
  finalized: true,
};

export const PUBLISHING_STAGES: StringMap = {
  preview: 'Preview',
  standard: 'Standard',
  finalized: 'Finalized',
};

export const QUALITY_CATEGORIES_DEFAULT: BooleanMap = {
  standard: true,
  test: false,
};

export const QUALITY_CATEGORIES: StringMap = {
  standard: 'Standard',
  test: 'Test',
};

const UNIT_SYMBOLS: StringMap = {
  percent: '%',
  meters: 'm',
  degrees: '°',
};

// TODO: Get a canonical list of field filter types
// https://www.planet.com/docs/reference/data-api/search-api/

export const DEFAULT_VALUES = {
  [KEYS.CLOUD_COVER]: [0, 1],
  [KEYS.GSD]: [0.1, 30],
  [KEYS.OFF_NADIR_ANGLE]: [-60, 60],
  [KEYS.SUN_AZIMUTH]: [0, 360],
  [KEYS.SUN_ELEVATION]: [0, 90],
  [KEYS.GROUND_CONTROL]: undefined,
  [KEYS.QUALITY_CATEGORY]: undefined,
};

export const CONFIGS = Object.freeze({
  [KEYS.CLOUD_COVER]: {
    id: KEYS.CLOUD_COVER,
    field_name: KEYS.CLOUD_COVER,
    type: 'RangeFilter',
    component: 'slider',
    range: [0, 1],
    defaults: DEFAULT_VALUES[KEYS.CLOUD_COVER],
    unit: UNIT_SYMBOLS.percent,
    scale: toPercent,
    resetScale: fromPercent,
    formatValues: formatPercentRange,
    name: 'Cloud cover',
    description: 'The portion of an image obscured by clouds.',
  },
  [KEYS.GSD]: {
    id: KEYS.GSD,
    field_name: KEYS.GSD,
    type: 'RangeFilter',
    component: 'slider',
    range: [0.1, 30],
    defaults: DEFAULT_VALUES[KEYS.GSD],
    unit: UNIT_SYMBOLS.meters,
    formatter: formatMeterValue,
    formatValues: formatMetersRange,
    name: 'Ground sample distance',
    description:
      'The expected size of one pixel estimated by mathematically combining knowledge of the optical characteristics of the telescope, the altitude of the satellite and the size and shape of the CCD sensor.',
  },
  [KEYS.OFF_NADIR_ANGLE]: {
    id: KEYS.OFF_NADIR_ANGLE,
    field_name: KEYS.OFF_NADIR_ANGLE,
    type: 'RangeFilter',
    component: 'slider',
    range: [-60, 60],
    defaults: DEFAULT_VALUES[KEYS.OFF_NADIR_ANGLE],
    unit: UNIT_SYMBOLS.degrees,
    formatter: formatDegrees,
    formatValues: roundValues,
    name: 'Off-nadir angle',
    description:
      'The angle between the nadir point and the actual point on the ground where the satellite is currently pointed. Standard Planet products and spacecraft operations are designed to minimize the off-nadir angle.',
  },
  [KEYS.SUN_AZIMUTH]: {
    id: KEYS.SUN_AZIMUTH,
    field_name: KEYS.SUN_AZIMUTH,
    type: 'RangeFilter',
    component: 'slider',
    range: [0, 360],
    defaults: DEFAULT_VALUES[KEYS.SUN_AZIMUTH],
    unit: UNIT_SYMBOLS.degrees,
    formatter: formatDegrees,
    formatValues: roundValues,
    name: 'Sun azimuth',
    description:
      'The solar azimuth angle is the azimuth angle of the sun. It defines in which direction the sun is, whereas the solar zenith angle or its complementary angle solar elevation defines how high the sun is.',
  },
  [KEYS.SUN_ELEVATION]: {
    id: KEYS.SUN_ELEVATION,
    field_name: KEYS.SUN_ELEVATION,
    type: 'RangeFilter',
    component: 'slider',
    range: [-90, 90],
    defaults: DEFAULT_VALUES[KEYS.SUN_ELEVATION],
    unit: UNIT_SYMBOLS.degrees,
    formatter: formatDegrees,
    formatValues: roundValues,
    name: 'Sun elevation',
    description:
      "The solar elevation angle is the altitude of the sun, the angle between the horizon and the centre of the sun's disc. The default angles are between 0° and 90°.",
  },
  [KEYS.GROUND_CONTROL]: {
    id: KEYS.GROUND_CONTROL,
    type: 'StringInFilter',
    component: 'toggle',
    field_name: KEYS.GROUND_CONTROL,
    defaults: DEFAULT_VALUES[KEYS.GROUND_CONTROL],
    name: 'Include only results with ground control',
    description: 'Only show me search results with ground control.',
    checked: 'true',
  },
  [KEYS.QUALITY_CATEGORY]: {
    id: KEYS.QUALITY_CATEGORY,
    type: 'StringInFilter',
    component: 'toggle',
    field_name: KEYS.QUALITY_CATEGORY,
    defaults: DEFAULT_VALUES[KEYS.QUALITY_CATEGORY],
    name: 'Show only standard image quality',
    description: 'Only show only search results with standard image quality.',
    checked: 'standard',
    learnMore:
      'https://developers.planet.com/docs/data/planetscope/#image-quality-standard-vs-test-imagery',
  },
});

export const EXCLUDED_FILTERS = {};

/** Utility functions */

export const hasChangedFromDefaults = (filter: Filter) => {
  const {values, defaults} = filter;

  if (values) {
    return defaults ? !isEqual(values, defaults) : true;
  }

  return false;
};

export const toAPIFilter = (filter: Filter): APIFilter => {
  const {type, values, field_name} = filter ?? {};

  let config: APIFilterConfig = [];
  if (type === 'StringInFilter') {
    config = (values ?? []).map(value => value as string).filter(v => !!v);
  } else if (type === 'RangeFilter' && values?.length === 2) {
    config = {
      gte: values[0],
      lte: values[1],
    };
  }

  return {
    type,
    config,
    ...(field_name ? {field_name} : {}),
  };
};

/**
 * exploit the naturally recursive nature of JSON.stringify to find the objects with
 * the specified key - value combination
 * This is helpful for loading a saved search and converting to our objects
 * https://stackoverflow.com/a/56204398
 * @param {*} entireObj
 * @param {*} keyToFind
 * @param {*} valToFind
 * @returns Array
 */
export const findNestedObj = (
  entireObj: object,
  keyToFind: string,
  valToFind: string,
) => {
  const foundObj: string[] = [];

  JSON.stringify(entireObj, (_, nestedValue) => {
    if (nestedValue && nestedValue[keyToFind] === valToFind) {
      foundObj.push(nestedValue);
    }
    return nestedValue;
  });

  return foundObj;
};

export const getGeometry = (
  filter: APIFilter | string | number,
): object | null => {
  if (typeof filter === 'object') {
    if (filter?.type === 'GeometryFilter') {
      return filter.config;
    }

    if (Array.isArray(filter?.config) && filter.config.length > 0) {
      for (let i = 0; i < filter.config.length; i++) {
        const result = getGeometry(filter.config[i]);
        if (result) {
          return result;
        }
      }
    }
  }

  return null;
};

export const dateRangesToISO = (dateRanges: DateRange[]) =>
  dateRanges.map(range => {
    const start = range[0]
      ? DateTime.fromISO(range[0], {zone: 'utc'})
      : DateTime.utc();

    const end = range[1]
      ? DateTime.fromISO(range[1], {zone: 'utc'})
      : DateTime.utc();

    return [start.startOf('day').toISO(), end.endOf('day').toISO()];
  });

export const createFilterModel = (
  field: string,
  operator: string,
  value: string | number,
  isDate?: boolean,
) => ({
  id: `${field}__${operator}`,
  field,
  operator,
  value:
    isDate && typeof value === 'string'
      ? DateTime.fromISO(value, {zone: 'utc'}).toJSDate()
      : value,
});

export const queryParamToFilterModel = (
  key: string,
  value: string | number,
  isDate = false,
) => {
  const field = key.replace(/__.*$/, ''); // Strip out operators

  if (isDate && typeof value === 'string') {
    if (value.indexOf('/') !== -1) {
      // Interval (Orders/Subscriptions API format)
      const [from, to] = value.split('/');
      if (from !== '..') {
        return createFilterModel(field, 'onOrAfter', from, true);
      }
      if (to !== '..') {
        return createFilterModel(field, 'onOrBefore', to, true);
      }
    } else {
      // Single date ('is' operator)
      return createFilterModel(field, 'is', value, true);
    }
  } else {
    const hasOperator = key.indexOf('__') !== -1;
    const operator = hasOperator ? key.split('__')[1] : 'is';

    return createFilterModel(field, operator, value);
  }
};
